import { Box, ButtonBase, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, Skeleton, Stack, TextField, Typography, Zoom, useMediaQuery } from '@mui/material';
import React, { useEffect, useLayoutEffect, useState } from 'react'
import styled from 'styled-components';
import { ReactComponent as CuponSVG } from "../../../assets/svg/cupon.svg";
import _ from 'lodash';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadingButtonStyled from '../../atoms/v2/LoadingButton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { FormDiscountTicket, FormDiscountTicketErrorCodes } from './CheckoutDialog/FormDiscountTicket';
import { FormPayment } from './CheckoutDialog/FormPayment';

const CheckoutDialog = (props: ICheckoutDialogProps) => {
    const { onClose, open, getOrderSummary, hasAtLeastOneCupon } = props;
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down(400));

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [step, setStep] = useState<'FIRST' | 'SECOND' | 'SECOND_FORCE'>('FIRST');
    const [orderSummary, setOrderSummary] = useState<Required<ICheckoutDialogOrderSummary> | undefined>(undefined);

    const onSubmit = async (code: string) => {
        const result = await getOrderSummary(code);
        if (result.isCodeValid && result.orderId !== undefined) {
            setOrderSummary({
                orderId: result.orderId,
                isCodeValid: result.isCodeValid,
                totalDiscountPercentPerCode: result.totalDiscountPercentPerCode,
                totalDiscountPercentPerTimer: result.totalDiscountPercentPerTimer,
                totalPrice: result.totalPrice,
                formCheckoutId: result.formCheckoutId,
                formCheckoutToken: result.formCheckoutToken
            });
            setStep('SECOND');
        }
        else throw FormDiscountTicketErrorCodes.INVALID_CODE;
    }

    useEffect(() => {
        const id = setTimeout(() => {
            props.onClose();
        }, 1000 * 60 * 60 * 2);

        return () => clearTimeout(id);
    }, [props.onClose]);

    useEffect(() => {
        if (open) {
            if (hasAtLeastOneCupon === true) {
                setStep('FIRST');
                setOrderSummary(undefined);
            } else {
                setIsLoading(true);
                const func = async () => {
                    try {
                        const result = await getOrderSummary('');
                        if (result.isCodeValid && result.orderId !== undefined) {
                            setOrderSummary({
                                orderId: result.orderId,
                                isCodeValid: result.isCodeValid,
                                totalDiscountPercentPerCode: result.totalDiscountPercentPerCode,
                                totalDiscountPercentPerTimer: result.totalDiscountPercentPerTimer,
                                totalPrice: result.totalPrice,
                                formCheckoutId: result.formCheckoutId,
                                formCheckoutToken: result.formCheckoutToken
                            });
                            setStep('SECOND_FORCE');
                        }
                        else throw FormDiscountTicketErrorCodes.INVALID_CODE;
                    } catch (error) {
                        onClose();
                    }
                }
                func().finally(() => {
                    setIsLoading(false);
                });
            }
        }
    }, [open, hasAtLeastOneCupon, getOrderSummary, onClose]);

    return (
        <BootstrapDialog
            open={open}
            fullWidth
            fullScreen={true}
            transitionDuration={0}
            TransitionProps={{
                onExited: () => {
                    setStep('FIRST');
                    setOrderSummary(undefined);
                }
            }}
        >
            <DialogContent sx={{
                maxWidth: "400px",
                width: "100%",
                p: 2,
                boxSizing: "border-box",
                alignSelf: 'center'
            }} >
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 5
                }}>
                    <div>
                        {step === 'SECOND' && (
                            <IconButtonStyled size="medium" onClick={() => setStep('FIRST')}>
                                <ArrowBackIcon fontSize="large" htmlColor='#32b0ea' />
                            </IconButtonStyled>
                        )}
                    </div>
                    <div></div>
                </Box>
                {isLoading === true && (
                    <Stack spacing={1}>
                        <Skeleton animation="pulse" variant="rounded" height={30} />
                        <Skeleton animation="pulse" variant="rounded" height={30} />
                        <Skeleton animation="pulse" variant="rounded" height={30} />
                    </Stack>
                )}
                {isLoading === false && (
                    <>
                        {step === 'FIRST' && (
                            <FormDiscountTicket
                                onSubmit={onSubmit} />
                        )}
                        {(
                            (step === 'SECOND' || step === 'SECOND_FORCE') &&
                            orderSummary !== undefined
                        ) && (
                                <FormPayment
                                    payload={orderSummary}
                                    onSuccess={props.onSuccess}
                                    onFailure={props.onFailure} />
                            )}
                    </>
                )}
            </DialogContent>
        </BootstrapDialog>
    );
}



const BootstrapDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        borderRadius: 20,
        maxWidth: 350
    },

    '& .MuiDialog-paperFullScreen': {
        borderRadius: 0,
        maxWidth: '100%'
    }
}));

const CssTextField = styled(TextField)({
    '& label.Mui-focused': {
        color: '#32b0ea',
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: 15,
        '&:hover fieldset': {
            borderColor: '#32b0ea',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#32b0ea',
        },
    },
});

const IconButtonStyled = styled(IconButton)({
    '&.MuiIconButton-root': {
        borderRadius: 15,
        borderWidth: 1,
        borderColor: '#32b0ea',
        borderStyle: 'solid'
    }
})

export { CheckoutDialog }